/**
 * Clinical Notes, ported from the design's residentsConsentAndNotes() (L30499-30838): a
 * SOAP draft from a month of Atlas care, the four-condition compliance gate, and
 * attest-and-sign held until the gate passes. Notes come from GET /clinical-notes (served);
 * the per-section evidence, the care-time log and the contact mix are served fields.
 */
import type { ClinicalNote, ClinicalNotesResponse, QualificationRow } from '@aithora/shared';
import { pre } from './dom';

type Ev = { preventDefault?: () => void } | undefined;
const INK = '#22272E', MUTE = '#54606C', BLUE = '#2F5C97', RED = '#B3261E', AMBER = '#4A5866', OK = '#1F6B52', VIO = '#5B6B7C';
const TONE: Record<string, string> = { '': INK, red: RED, amber: AMBER, ok: OK, mute: MUTE, blue: BLUE, vio: VIO };
const mark = (tone: string) =>
  'flex:0 0 auto;display:inline-flex;align-items:center;height:16px;padding:0 6px;border-radius:3px;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;color:' +
  (TONE[tone] || MUTE) + ';background:' + (TONE[tone] || MUTE) + '1A;';
const REASONS = ['Inference not supported', 'Medication content wrong', 'Missing something that happened', 'Wording unsafe for a chart'];
const S_TONE: Record<string, string> = { SIGNED: 'ok', READY: 'ok', 'NOT BILLABLE': 'amber', 'SENT BACK': 'mute' };
const S_MARK: Record<string, string> = { SIGNED: '●', READY: '●', 'NOT BILLABLE': '◆', 'SENT BACK': '○' };
const MONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dm = (iso: string) => { const d = new Date(iso); return d.getDate() + ' ' + MONS[d.getMonth()]; };
const nSpark = (pts: number[]) => {
  if (!pts.length) return '';
  const mn = Math.min(...pts), mx = Math.max(...pts), rg = mx - mn || 1;
  return pts.map((v, i) => (i ? 'L' : 'M') + (2.5 + (i / Math.max(1, pts.length - 1)) * 43).toFixed(1) + ' ' + (15.5 - ((v - mn) / rg) * 13).toFixed(1)).join(' ');
};
const clockOf = (t: number) => { const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60); return h > 0 ? h + 'h ' + m + 'm' : m + 'm ' + (t % 60) + 's'; };

/** A note is in the queue until it is signed or sent back. */
const isSigned = (n: ClinicalNote) => /attest|sign/i.test(n.status);
const isReturned = (n: ClinicalNote) => /return|sent_back/i.test(n.status);

/** SOAP sections: the served `sections`, else the served text split on "S:", "O:", "A:", "P:". */
export function sectionsOf(n: ClinicalNote): NonNullable<ClinicalNote['sections']> {
  if (n.sections && n.sections.length) return n.sections;
  const out: NonNullable<ClinicalNote['sections']> = [];
  const re = /(?:^|\n)\s*(S|O|A|P)(?:ubjective|bjective|ssessment|lan)?\s*[:.]\s*([\s\S]*?)(?=\n\s*(?:S|O|A|P)(?:ubjective|bjective|ssessment|lan)?\s*[:.]|$)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(n.soap ?? ''))) out.push({ tag: m[1] as 'S' | 'O' | 'A' | 'P', body: m[2].trim(), evidence: [] });
  return out;
}

export interface NotesUi {
  sel: string | null;
  filter: string | null;
  open: Record<string, boolean>;
  allOpen: boolean;
  edits: Record<string, string>;
  editing: string | null;
  backFor: string | null;
  backWhy: string;
  composing: string | null;
  logged: Record<string, number>;
}

export function notesVals(opts: {
  data: ClinicalNotesResponse;
  ui: NotesUi;
  setUi: (p: Partial<NotesUi>) => void;
  canWrite: boolean;
  /** The read-only seat's name (V9 "Read-only as Super Admin"). */
  roleLabel?: string;
  elapsedSec: number;
  generate: (n: ClinicalNote) => void;
  sign: (n: ClinicalNote, edits: Record<string, string>) => void;
  signMany: (ns: ClinicalNote[]) => void;
  sendBack: (n: ClinicalNote, reason: string) => void;
  addendum: (n: ClinicalNote) => void;
  logMinutes: (n: ClinicalNote, minutes: number) => void;
  /** Record the resident's CCM / BHI election (restored from production; not in the design). */
  recordElection?: (n: ClinicalNote) => void;
  /**
   * Live qualification for this resident and code this month (GET /capture/qualification):
   * the threshold from the code table and what else the claim still needs. Absent while it
   * loads or for a reader without the building's books; the gate then uses CMS's 20.
   */
  qualFor?: (residentId: string, code: string) => QualificationRow | undefined;
  go: (path: string) => void;
}) {
  const { data, ui, setUi } = opts;
  const all = data.notes;
  const priorOf = (n: ClinicalNote) =>
    all.filter((x) => x !== n && x.resident_id === n.resident_id && isSigned(x) && x.attested_at && x.id !== n.id && x.period < n.period)
      .sort((a, b) => Date.parse(b.attested_at!) - Date.parse(a.attested_at!)).slice(0, 3);
  // The queue: this period's notes (the design's N_ORDER), prior signed periods are history.
  const period = all.reduce((p, n) => (n.period > p ? n.period : p), '');
  const Q = all.filter((n) => n.period === period);
  const mins = (n: ClinicalNote) => n.compliance.careTimeMinutes + (ui.logged[n.id] || 0);
  // The month's minutes threshold for the note's code, from the server's code table.
  const needOf = (n: ClinicalNote) => opts.qualFor?.(n.resident_id, n.cpt_code)?.minutes?.need ?? 20;
  const minutesWhy = (n: ClinicalNote, m: number, need: number): string => {
    if (m < need) return m + ' of ' + need + ' minutes: ' + (need - m) + ' more to qualify';
    const q = opts.qualFor?.(n.resident_id, n.cpt_code);
    if (q?.qualified) return q.label;
    const rest = (q?.stillNeeded ?? []).filter((x) => x.gate !== 'minutes').map((x) => x.text);
    return rest.length ? 'Minutes met: still needs ' + rest.join('; ') : 'Met across the month';
  };
  const gateOf = (n: ClinicalNote, m: number): Array<[string, boolean, string, string, number]> => {
    const c = n.compliance;
    const need = needOf(n);
    return [
      ['Election consent', c.consentOnFile, c.consentOnFile ? 'On file and current' : 'Not on file for this resident', c.consentOnFile ? 'YES' : 'NO', 100],
      ['Two or more chronic diagnoses', c.chronicConditionCount >= 2, c.chronicConditionCount + ' active chronic ' + (c.chronicConditionCount === 1 ? 'diagnosis' : 'diagnoses'), String(c.chronicConditionCount), Math.min(100, (c.chronicConditionCount / 2) * 100)],
      ['Active care plan', c.carePlanActive, c.carePlanActive ? 'Current, reviewed this quarter' : 'No active plan on record', c.carePlanActive ? 'YES' : 'NO', 100],
      [need === 20 ? 'Twenty minutes of care' : need + ' minutes of care', m >= need, minutesWhy(n, m, need), m + 'm', Math.min(100, (m / need) * 100)],
    ];
  };
  const codeOf = (n: ClinicalNote, m: number) => { const need = needOf(n); return m >= need && n.compliance.chronicConditionCount >= 2 && n.compliance.carePlanActive ? '99490' : m >= need && n.compliance.carePlanActive ? '99484' : null; };
  const gateFor = (n: ClinicalNote) => { const m = mins(n); const code = codeOf(n, m); return gateOf(n, m).filter((g) => !(code === '99484' && g[0].indexOf('chronic') >= 0)); };
  const stateOf = (n: ClinicalNote) => (isSigned(n) ? 'SIGNED' : isReturned(n) ? 'SENT BACK' : gateFor(n).filter((x) => x[1]).length === 4 ? 'READY' : 'NOT BILLABLE');
  const openN = Q.filter((n) => !isSigned(n) && !isReturned(n));
  const sel = Q.find((n) => n.id === ui.sel) ?? openN[0] ?? Q[0] ?? null;
  const nameOf = (n: ClinicalNote) => (n.resident_name ?? '–') + (n.addendumOf ? ' · addendum' : '');
  const month = data.month ?? null;

  const base = {
    nPickOpts: Q.map((n) => ({ value: n.id, label: (n.resident_name ?? '–') + (n.addendumOf ? ' · addendum' : ' · room ' + (n.room ?? '–')) })),
    nPick: sel?.id ?? '',
    nPickChange: (e: { target: { value: string } }) => setUi({ sel: e.target.value }),
    nGenLabel: ui.composing ? 'Drafting…' : 'Generate draft',
    nGenStyle: 'flex:0 0 auto;display:inline-flex;align-items:center;height:32px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
      (ui.composing ? 'background:rgba(47,92,151,.14);color:#1D3F6B;' : 'background:' + BLUE + ';color:#FCFAF4;'),
    nGen: (e: Ev) => { pre(e); if (sel && !ui.composing && opts.canWrite) opts.generate(sel); },
    nGenNote: 'Composed from a month of Atlas care. A draft is not documentation until a clinician signs it.',
    nQCount: openN.length + ' of ' + Q.length + ' waiting',
    nQEmpty: Q.filter((n) => !ui.filter || (ui.filter === 'Waiting' ? openN.includes(n) : stateOf(n) === ui.filter.toUpperCase())).length === 0,
    nEmptyTitle: ui.filter ? 'Nothing is ' + ui.filter.toLowerCase() + ' right now.' : 'Nothing is waiting on you.',
    nEmptyColor: ui.filter ? INK : '#12503C',
    nEmptyNote: ui.filter ? 'Clear the filter to see the rest of the queue.' : 'Every drafted note has been read and signed. An empty queue is the good outcome.',
    nChips: ['Waiting', 'Ready', 'Not billable'].map((k) => {
      const n = k === 'Waiting' ? openN.length : Q.filter((x) => stateOf(x) === k.toUpperCase()).length;
      const on = ui.filter === k;
      return {
        label: k, n: String(n),
        onClick: (e: Ev) => { pre(e); setUi({ filter: ui.filter === k ? null : k }); },
        dot: 'width:6px;height:6px;border-radius:50%;display:inline-block;background:' + (k === 'Not billable' ? AMBER : k === 'Ready' ? OK : MUTE) + ';',
        style: 'display:inline-flex;align-items:center;gap:5px;height:25px;padding:0 9px;border-radius:13px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;' +
          (on ? 'background:rgba(47,92,151,.13);color:#1D3F6B;' : 'background:rgba(34,39,46,.05);color:' + MUTE + ';'),
      };
    }),
    nQueue: Q.filter((n) => !ui.filter || (ui.filter === 'Waiting' ? openN.includes(n) : stateOf(n) === ui.filter.toUpperCase())).map((n, i) => {
      const stt = stateOf(n);
      const m = mins(n);
      const wait = (n.waitingSeconds ?? Math.max(0, Math.round((Date.now() - Date.parse(n.created_at)) / 1000))) + opts.elapsedSec;
      return {
        rank: String(i + 1), name: nameOf(n), state: stt, mins: m + 'm of 20',
        badge: S_MARK[stt] + ' ' + stt, badgeStyle: mark(S_TONE[stt]),
        clock: isSigned(n) ? 'sealed' : isReturned(n) ? 'returned' : clockOf(wait),
        clockColor: isSigned(n) ? OK : isReturned(n) ? MUTE : wait > 21600 ? RED : AMBER,
        d: nSpark(n.contactMix?.[0]?.history ?? []), sparkC: stt === 'NOT BILLABLE' ? AMBER : OK,
        fill: 'display:block;height:100%;border-radius:2px;width:' + Math.min(100, (m / 20) * 100) + '%;background:' + (m >= 20 ? OK : AMBER) + ';transform-origin:left;animation:growW .5s cubic-bezier(.22,.61,.36,1) forwards;',
        mine: n.ownerName ? n.ownerName.toUpperCase() : 'YOURS',
        mineStyle: mark(n.ownerName ? 'mute' : 'blue'),
        onClick: (e: Ev) => { pre(e); setUi({ sel: n.id }); },
        rankStyle: 'font-size:14px;color:' + MUTE + ';font-variant-numeric:tabular-nums;',
        rowStyle: 'display:grid;grid-template-columns:14px minmax(0,1fr) 40px 52px;gap:8px;align-items:center;min-width:0;padding:9px 0 9px 9px;border-left:2px solid ' +
          (sel?.id === n.id ? BLUE : 'rgba(34,39,46,.14)') + ';border-top:1px solid rgba(34,39,46,.12);text-decoration:none;color:' + (sel?.id === n.id ? BLUE : INK) + ';' +
          (isSigned(n) || isReturned(n) ? 'opacity:.6;' : '') + 'transition:border-color .15s cubic-bezier(.22,.61,.36,1);',
      };
    }),
    nMonthNote: month?.label ?? 'this month · your signatures',
    nMonth: [
      ['signed', String(month?.signed ?? Q.filter(isSigned).length), '', OK],
      ['minutes', String(month?.minutes ?? Q.filter((n) => !n.addendumOf).reduce((t, n) => t + mins(n), 0)), 'billed', INK],
      ['returned', String(month?.returned ?? Q.filter(isReturned).length), '', AMBER],
    ].map((m) => ({ k: m[0], n: m[1], unit: m[2], color: m[3] })),
    nReturned: (month?.returnedWhy ?? []).map((b) => ({ why: b.why, n: String(b.n), onClick: (e: Ev) => { pre(e); setUi({ filter: 'Not billable' }); } })),
  };
  if (!sel) {
    return { ...base, nTitle: 'No notes this period', nState: '', nStateStyle: 'display:none;', nMeta: '', nRisk: false, nHasGaps: false, nGaps: [], nFed: [], nPass: '0', nTotal: '4', nRing: 'display:none;', nHole: '', nRingNum: '', nBand: '', nBandStyle: 'display:none;', nCptChip: '', nCptStyle: 'display:none;', nGateLede: '', nGateRows: [], nDraftChip: '', nDraftChipStyle: 'display:none;', nExpandLabel: '', nExpandAll: () => {}, nSoap: [], nSignLabel: '', nSignTitle: '', nSignStyle: 'display:none;', nSign: () => {}, nSignHint: '', nMinsTotal: '0 minutes', nLogNote: '', nLog: [], nPriorNote: '', nPriorEmpty: true, nFirst: '', nPrior: [], nCanAddendum: false, nAddendum: () => {}, nCanBatch: false, nBatchLabel: '', nBatch: () => {}, nCanSendBack: false, nBack: () => {}, nBackOpen: false, nReasons: [], nBackConfirm: () => {}, nBackCancel: () => {}, nEscLabel: '', nEscStyle: '', nEscalate: () => {}, nRiskLabel: '', nRiskStyle: '', nCanAct: false, nRoShow: false, nRoLine: '' };
  }
  const D = sel;
  const m = mins(D);
  const gate = gateFor(D);
  const passN = gate.filter((g) => g[1]).length;
  const gateOk = passN === 4;
  const code = codeOf(D, m);
  const signed = isSigned(D);
  const sent = isReturned(D);
  const composing = ui.composing === D.id;
  const secs = sectionsOf(D);
  const editKeys = Object.keys(ui.edits).filter((k) => k.indexOf(D.id + ':') === 0);
  const prior = priorOf(D);
  const ready = openN.filter((n) => !n.redFlag && gateFor(n).every((g) => g[1]));
  return {
    ...base,
    nTitle: (D.resident_name ?? '–') + ' · ' + (D.addendumOf ? 'addendum' : D.noteType ?? 'Care note'),
    nState: signed ? '● IN THE RECORD' : sent ? '○ SENT BACK' : gateOk ? '● READY TO SIGN' : '◆ NOT BILLABLE',
    nStateStyle: mark(signed ? 'ok' : sent ? 'mute' : gateOk ? 'ok' : 'amber'),
    nMeta: 'room ' + (D.room ?? '–') + ' · ' + (signed ? 'signed and sealed' : 'not in the record') + ' · ' + m + ' minutes of care this month',
    nRisk: !!D.redFlag,
    nRiskLabel: signed ? '▲ SECOND REVIEW PENDING' : '▲ RED FLAG',
    nRiskStyle: mark('red'),
    nEscLabel: 'Escalate with SBAR',
    nEscStyle: 'flex:0 0 auto;display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;background:rgba(74,88,102,.13);color:#4A5866;',
    nEscalate: (e: Ev) => { pre(e); opts.go('/guardian'); },
    nHasGaps: (D.gaps ?? []).length > 0,
    nGaps: D.gaps ?? [],
    nFed: (D.contactMix ?? []).map((c) => ({
      name: c.name, pct: c.share ? c.share + '%' : '–', pctColor: c.share ? INK : MUTE,
      wrap: 'flex:1 1 118px;min-width:104px;display:flex;align-items:center;gap:6px;padding:3px 0;' + (c.share ? '' : 'opacity:.5;'),
      dot: 'flex:0 0 7px;width:7px;height:7px;border-radius:50%;background:' + (c.share ? BLUE : MUTE) + ';',
      bars: c.history.slice(3).map((v, i) => 'display:block;width:3px;border-radius:1px;height:' + Math.max(2, Math.round((v / 62) * 11)) + 'px;background:' + (c.share ? BLUE : MUTE) +
        ';opacity:' + (0.4 + i * 0.12).toFixed(2) + ';transform-origin:bottom;animation:growH .45s cubic-bezier(.22,.61,.36,1) both;animation-delay:' + i * 40 + 'ms;'),
    })),
    nPass: String(passN),
    nTotal: String(gate.length),
    nRing: 'flex:0 0 62px;width:62px;height:62px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:conic-gradient(' + (gateOk ? OK : AMBER) + ' ' +
      Math.round((passN / gate.length) * 360) + 'deg, rgba(34,39,46,.10) 0);transition:background .4s cubic-bezier(.22,.61,.36,1);animation:popIn .4s cubic-bezier(.22,.61,.36,1) both;',
    nHole: 'width:46px;height:46px;border-radius:50%;background:#F6F3EC;display:flex;flex-direction:column;align-items:center;justify-content:center;font-style:normal;',
    nRingNum: 'display:block;font-size:19px;font-weight:700;letter-spacing:-.02em;font-style:normal;line-height:1;color:' + (gateOk ? OK : AMBER) + ';',
    nBand: gateOk ? '● PASSES' : '◆ HOLDS',
    nBandStyle: mark(gateOk ? 'ok' : 'amber'),
    nCptChip: code ? code + ' ELIGIBLE' : 'NO CODE YET',
    nCptStyle: mark(code ? 'blue' : 'mute'),
    nGateLede: gateOk ? 'All conditions met. Signing puts this in the record and supports the claim.' : 'Attest and sign stays held until every condition is met. Nothing documents a claim before that.',
    nGateRows: gate.map((g) => ({
      name: g[0], why: g[2], val: g[3],
      mark: g[1] ? '●' : '◆',
      markStyle: 'font-size:14px;color:' + (g[1] ? OK : AMBER) + ';',
      valColor: g[1] ? OK : AMBER,
      rowStyle: 'display:grid;grid-template-columns:13px minmax(0,1.7fr) minmax(34px,.6fr) 66px;gap:9px;align-items:center;min-width:0;padding:5px 0;border-top:1px solid rgba(34,39,46,.1);',
      fill: 'display:block;height:100%;border-radius:2px;width:' + g[4] + '%;background:' + (g[1] ? OK : AMBER) + ';transform-origin:left;animation:growW .5s cubic-bezier(.22,.61,.36,1) forwards;',
      canFix: !g[1] && g[0].indexOf('minutes') >= 0 && opts.canWrite,
      canElect: !g[1] && g[0] === 'Election consent' && opts.canWrite && !!opts.recordElection && !isSigned(D),
      elect: (e: Ev) => { pre(e); opts.recordElection?.(D); },
      logs: [5, 10, Math.max(1, needOf(D) - m)].filter((v, k, a) => a.indexOf(v) === k).map((v) => ({
        label: '+' + v, title: 'Log ' + v + ' minutes of care',
        onClick: (e: Ev) => { pre(e); opts.logMinutes(D, v); },
      })),
    })),
    nDraftChip: composing ? '◆ DRAFTING' : signed ? '● SIGNED' : D.addendumOf ? '◆ ADDENDUM' : '◆ DRAFT',
    nDraftChipStyle: mark(composing ? 'blue' : signed ? 'ok' : D.addendumOf ? 'blue' : 'amber'),
    nExpandLabel: ui.allOpen ? 'Collapse all' : 'Show sources',
    nExpandAll: (e: Ev) => { pre(e); setUi({ allOpen: !ui.allOpen }); },
    nSoap: secs.map((sc) => {
      const open = ui.allOpen || ui.open[D.id + sc.tag] === true;
      const key = D.id + ':' + sc.tag;
      const edited = ui.edits[key] !== undefined && ui.edits[key] !== sc.body;
      const editing = ui.editing === key && !signed && !sent;
      const body = ui.edits[key] !== undefined ? ui.edits[key] : sc.body;
      return {
        tag: sc.tag, body, open, chev: open ? '−' : '+',
        reading: !editing, editing,
        canEdit: !signed && !sent && opts.canWrite,
        editGlyph: editing ? 'Done' : 'Edit',
        editTitle: editing ? 'Stop editing this section' : 'Correct this section before you sign it',
        editStyle: 'display:inline-flex;align-items:center;height:20px;padding:0 7px;border-radius:5px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
          (editing ? 'background:' + BLUE + ';color:#FCFAF4;' : 'background:rgba(47,92,151,.10);color:#1D3F6B;'),
        onEditToggle: (e: Ev) => { pre(e); setUi({ editing: ui.editing === key ? null : key }); },
        onEdit: (e: { target: { value: string } }) => setUi({ edits: { ...ui.edits, [key]: e.target.value } }),
        edited,
        editedStyle: mark('blue'),
        editedNote: 'Your wording, not the draft’s. Both versions are kept, and the change is sealed with your signature.',
        onRevert: (e: Ev) => { pre(e); const x = { ...ui.edits }; delete x[key]; setUi({ edits: x, editing: null }); },
        clamp: open ? '' : 'display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;',
        tagStyle: 'flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:5px;font-size:14px;font-weight:800;color:' + BLUE +
          ';background:rgba(47,92,151,.12);animation:riseIn .3s cubic-bezier(.22,.61,.36,1);',
        onToggle: (e: Ev) => { pre(e); setUi({ open: { ...ui.open, [D.id + sc.tag]: !open }, allOpen: false }); },
        ev: sc.evidence.map((x) => ({
          k: x.k, v: x.v,
          onClick: (e: Ev) => {
            pre(e);
            opts.go(x.k.indexOf('Atlas') === 0 || x.k.indexOf('Atlas') === 0 ? '/atlas?tab=conversations' : x.k.indexOf('Truth') === 0 ? '/oversight' : x.k.indexOf('Consent') === 0 ? '/residents'
              : x.k.indexOf('MAR') === 0 || x.k.indexOf('Vitals') === 0 ? '/worklist' : x.k.indexOf('SBAR') === 0 ? '/guardian' : '/residents');
          },
        })),
      };
    }),
    nSignLabel: signed ? 'Signed' : 'Attest and sign',
    nSignTitle: gateOk ? 'Puts it in the record under your name' : 'The compliance gate has not passed',
    nSignStyle: 'flex:0 0 auto;display:inline-flex;align-items:center;height:30px;padding:0 14px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;transition:background .2s cubic-bezier(.22,.61,.36,1);' +
      (signed ? 'background:rgba(31,107,82,.12);color:' + OK + ';' : gateOk ? 'background:' + BLUE + ';color:#FCFAF4;' : 'background:rgba(34,39,46,.09);color:#54606C;cursor:not-allowed;'),
    nSign: (e: Ev) => {
      pre(e);
      if (!gateOk || signed || !opts.canWrite) return;
      opts.sign(D, Object.fromEntries(editKeys.map((k) => [k.split(':')[1], ui.edits[k]])));
    },
    nSignHint: signed ? 'In the record under your name. A correction from here is an addendum, never an edit to this one.'
      : D.addendumOf ? 'This is an addendum. The signed note above it stays in the record exactly as it was.'
        : editKeys.length ? 'You changed ' + editKeys.length + ' section' + (editKeys.length === 1 ? '' : 's') + '. Signing seals your wording, not the draft’s.'
          : sent ? 'It has left your queue with a reason on it.'
            : gateOk ? 'Signing writes it under your name and seals it.'
              : 'Held until the gate passes. ' + (D.redFlag ? 'This draft also routes to a second reviewer.' : ''),
    nMinsTotal: m + ' minutes',
    nLogNote: 'what a reviewer asks for',
    nLog: (D.careTimeLog ?? []).map((c) => ({ when: c.when, who: c.who, what: c.what, mins: c.minutes + 'm', onClick: (e: Ev) => { pre(e); opts.go('/' + (c.view || 'worklist')); } })),
    nPriorNote: 'last three, sealed',
    nPriorEmpty: prior.length === 0,
    nFirst: D.resident_name ?? '–',
    nPrior: prior.map((p) => ({ when: dm(p.attested_at!), code: p.cpt_code, what: p.title ?? p.noteType ?? 'Care note', seal: p.sealRef ?? '–', onClick: (e: Ev) => { pre(e); opts.go('/oversight'); } })),
    // V9: read-only roles get no sign, escalate, send back or addendum, only a line saying so.
    nCanAct: opts.canWrite,
    nRoShow: !opts.canWrite,
    nRoLine: 'Read-only as ' + (opts.roleLabel ?? 'your role') + '. The writer and the nurse sign notes.',
    nCanAddendum: signed && !D.addendumOf && opts.canWrite,
    nAddendum: (e: Ev) => { pre(e); opts.addendum(D); },
    nCanBatch: ready.length >= 1 && opts.canWrite,
    nBatchLabel: 'Attest and sign ' + ready.length + ' with no red flag',
    nBatch: (e: Ev) => { pre(e); opts.signMany(ready); },
    nCanSendBack: !signed && !sent && opts.canWrite,
    nBack: (e: Ev) => { pre(e); setUi({ backFor: ui.backFor === D.id ? null : D.id, backWhy: REASONS[0] }); },
    nBackOpen: ui.backFor === D.id && !signed,
    nReasons: REASONS.map((x) => ({
      label: x,
      onClick: (e: Ev) => { pre(e); setUi({ backWhy: x }); },
      style: 'display:inline-flex;align-items:center;height:28px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
        ((ui.backWhy || REASONS[0]) === x ? 'background:' + RED + ';color:#FCFAF4;' : 'background:rgba(179,38,30,.10);color:' + RED + ';'),
    })),
    nBackConfirm: (e: Ev) => { pre(e); setUi({ backFor: null }); opts.sendBack(D, ui.backWhy || REASONS[0]); },
    nBackCancel: (e: Ev) => { pre(e); setUi({ backFor: null }); },
  };
}
